<script setup lang="ts">
  import {
    PdfFormTemplatesService,
    OrganisationsService,
    type PDFFormTemplate,
    type TOrganisation
  } from '@/types/generated'
  import PaginatedTableView, {
    type FetchDataOptions,
    type PaginatedResponse
  } from '@/components/admin/PaginatedTable.vue'
  import { ROUTES } from '@/router/routes'
  import UniqueTableDataCell from '@/components/admin/table/UniqueTableDataCell.vue'
  import TableOptionsButton from '@/components/admin/table/TableOptionsButton.vue'
  import Breadcrumbs from '@/components/admin/breadcrumbs/Breadcrumbs.vue'
  import { TableRow, TableCell } from '@/components/ui/table'
  import { useRouter } from 'vue-router'
  import { ref, computed } from 'vue'
  import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle
  } from '@/components/ui/dialog'
  import { Button } from '@/components/ui/button'
  import { genericErrorToast } from '@/lib/toasts'
  import { useTranslation } from '@/composables/useTranslation'
  import CustomerDisplay from '@/components/admin/CustomerDisplay.vue'
  import SelectField, { type Option } from '@/components/admin/form/SelectField.vue'

  const { t } = useTranslation({
    en: {
      dashboard: 'Dashboard',
      documentTemplates: 'Document Templates',
      createTemplate: 'Create template',
      uuid: 'UUID',
      name: 'Name',
      organization: 'Organization',
      formFields: 'Form Fields',
      created: 'Created',
      noOrganization: 'No organization',
      edit: 'Edit',
      delete: 'Delete',
      cancel: 'Cancel',
      confirmDeletion: 'Confirm Deletion',
      confirmDeleteMessage:
        'Are you sure you want to delete the template "{name}"? This action cannot be undone.',
      reassignOrganization: 'Reassign Organization',
      reassignOrgTitle: 'Reassign Organization',
      reassignOrgDescription: 'Select a new organization for the template "{name}".',
      selectOrganization: 'Select organization',
      confirm: 'Confirm'
    },
    de: {
      dashboard: 'Dashboard',
      documentTemplates: 'Dokumentenvorlagen',
      createTemplate: 'Vorlage erstellen',
      uuid: 'UUID',
      name: 'Name',
      organization: 'Organisation',
      formFields: 'Formularfelder',
      created: 'Erstellt',
      noOrganization: 'Keine Organisation',
      edit: 'Bearbeiten',
      delete: 'Löschen',
      cancel: 'Abbrechen',
      confirmDeletion: 'Löschen bestätigen',
      confirmDeleteMessage:
        'Sind Sie sicher, dass Sie die Vorlage "{name}" löschen möchten? Diese Aktion kann nicht rückgängig gemacht werden.',
      reassignOrganization: 'Organisation neu zuweisen',
      reassignOrgTitle: 'Organisation neu zuweisen',
      reassignOrgDescription: 'Wählen Sie eine neue Organisation für die Vorlage "{name}".',
      selectOrganization: 'Organisation auswählen',
      confirm: 'Bestätigen'
    }
  })
  const router = useRouter()
  const showDeleteModal = ref(false)
  const templateToDelete = ref<PDFFormTemplate | null>(null)
  const paginatedTableRef = ref<InstanceType<typeof PaginatedTableView> | null>(null)

  const showReassignOrgModal = ref(false)
  const templateToReassign = ref<PDFFormTemplate | null>(null)
  const selectedOrgUuid = ref<string | null>(null)
  const organisations = ref<TOrganisation[]>([])
  const isReassigning = ref(false)

  const organizationOptions = computed<Option[]>(() => {
    const options: Option[] =
      organisations.value?.map((org: TOrganisation) => ({
        value: org.uuid,
        title: org.name || ''
      })) || []

    const currentOrg = (templateToReassign.value as any)?.org
    if (currentOrg && !options.find((opt: Option) => opt.value === currentOrg.uuid)) {
      options.unshift({
        value: currentOrg.uuid,
        title: currentOrg.name || ''
      })
    }

    return options
  })

  const fetchOrganizations = async (query?: string) => {
    try {
      const result = await OrganisationsService.organisationsList({
        limit: query ? 0 : 20,
        search: query || undefined
      })
      organisations.value = result.results as TOrganisation[]
    } catch (error) {
      genericErrorToast()
    }
  }

  const searchOrganizations = async (query: string, oldQuery: string) => {
    if (query !== oldQuery) {
      await fetchOrganizations(query)
    }
  }

  const fetchTemplates = async (
    options?: FetchDataOptions
  ): Promise<PaginatedResponse<PDFFormTemplate>> => {
    const response = await PdfFormTemplatesService.pdfFormTemplatesList({
      limit: options?.limit,
      offset: options?.offset,
      search: options?.search
    })

    return response as unknown as PaginatedResponse<PDFFormTemplate>
  }

  const navigateToCreate = () => {
    router.push(ROUTES.ADMIN.PDF_FORM_TEMPLATES.PDF_FORM_TEMPLATES_CREATE)
  }

  const openDeleteModal = (template: PDFFormTemplate) => {
    templateToDelete.value = template
    showDeleteModal.value = true
  }

  const closeDeleteModal = () => {
    showDeleteModal.value = false
    templateToDelete.value = null
  }

  const confirmDelete = async () => {
    if (templateToDelete.value) {
      try {
        await PdfFormTemplatesService.pdfFormTemplatesDestroy({
          uuid: templateToDelete.value.uuid
        })
        closeDeleteModal()
        if (paginatedTableRef.value) {
          await paginatedTableRef.value.refreshData()
        }
      } catch (error) {
        genericErrorToast()
      }
    }
  }

  const openReassignOrgModal = async (template: PDFFormTemplate) => {
    templateToReassign.value = template
    selectedOrgUuid.value = (template as any).org?.uuid || null
    showReassignOrgModal.value = true
    await fetchOrganizations()
  }

  const closeReassignOrgModal = () => {
    showReassignOrgModal.value = false
    templateToReassign.value = null
    selectedOrgUuid.value = null
  }

  const confirmReassignOrg = async () => {
    if (templateToReassign.value && selectedOrgUuid.value) {
      isReassigning.value = true
      try {
        await PdfFormTemplatesService.pdfFormTemplatesPartialUpdate({
          uuid: templateToReassign.value.uuid,
          requestBody: {
            org_uuid: selectedOrgUuid.value
          }
        })
        closeReassignOrgModal()
        if (paginatedTableRef.value) {
          await paginatedTableRef.value.refreshData()
        }
      } catch (error) {
        genericErrorToast()
      } finally {
        isReassigning.value = false
      }
    }
  }
</script>

<template>
  <div>
    <div class="space-y-5 p-2 sm:p-5 sm:py-0 md:pt-5">
      <!-- Page Header -->
      <div class="flex items-center justify-between gap-x-5">
        <Breadcrumbs
          :items="[
            { title: t('dashboard'), to: ROUTES.ADMIN.DASHBOARD },
            {
              title: t('documentTemplates'),
              to: ROUTES.ADMIN.PDF_FORM_TEMPLATES.PDF_FORM_TEMPLATES_LIST
            }
          ]"
        />
        <div class="flex items-center justify-end gap-x-2">
          <Button
            variant="default"
            @click="navigateToCreate"
          >
            <svg
              class="hidden size-3 flex-shrink-0 sm:block"
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              fill="currentColor"
              viewBox="0 0 16 16"
            >
              <path
                fill-rule="evenodd"
                clip-rule="evenodd"
                d="M8 1C8.55228 1 9 1.44772 9 2V7L14 7C14.5523 7 15 7.44771 15 8C15 8.55228 14.5523 9 14 9L9 9V14C9 14.5523 8.55228 15 8 15C7.44772 15 7 14.5523 7 14V9.00001L2 9.00001C1.44772 9.00001 1 8.5523 1 8.00001C0.999999 7.44773 1.44771 7.00001 2 7.00001L7 7.00001V2C7 1.44772 7.44772 1 8 1Z"
              ></path>
            </svg>
            {{ t('createTemplate') }}
          </Button>
        </div>
      </div>
      <!-- End Page Header -->

      <!-- Templates Table Card -->
      <div class="flex flex-col space-y-4 rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
        <div>
          <!-- Tab Content -->
          <div class="">
            <!-- Table Section -->
            <div
              class="[&amp;::-webkit-scrollbar]:h-2 [&amp;::-webkit-scrollbar-thumb]:rounded-full
                [&amp;::-webkit-scrollbar-track]:bg-gray-100 [&amp;::-webkit-scrollbar-thumb]:bg-gray-300
                &amp;::-webkit-scrollbar-track]:bg-neutral-700 &amp;::-webkit-scrollbar-thumb]:bg-neutral-500
                overflow-x-auto"
            >
              <div class="inline-block min-w-full align-middle">
                <!-- Table -->
                <PaginatedTableView
                  ref="paginatedTableRef"
                  :fetchData="fetchTemplates"
                  searchableFields="UUID, name"
                >
                  <template #table-head>
                    <TableCell>{{ t('uuid') }}</TableCell>
                    <TableCell class="w-full max-w-md">{{ t('name') }}</TableCell>
                    <TableCell class="min-w-[200px]">{{ t('organization') }}</TableCell>
                    <TableCell>{{ t('formFields') }}</TableCell>
                    <TableCell>{{ t('created') }}</TableCell>
                    <TableCell></TableCell>
                  </template>
                  <template #table-row="{ items: templates }: { items: PDFFormTemplate[] }">
                    <TableRow
                      v-for="template in templates"
                      :key="template.uuid"
                    >
                      <UniqueTableDataCell :truncate="true">
                        <router-link
                          :to="
                            ROUTES.ADMIN.PDF_FORM_TEMPLATES.PDF_FORM_TEMPLATES_DETAIL.replace(
                              ':uuid',
                              template.uuid
                            )
                          "
                        >
                          {{ template.uuid }}
                        </router-link>
                      </UniqueTableDataCell>
                      <TableCell>
                        <div class="flex items-center gap-2">
                          <span>{{ template.name }}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div
                          v-if="(template as any).org"
                          class="flex items-center"
                        >
                          <CustomerDisplay
                            :org="(template as any).org"
                            :small="true"
                          />
                        </div>
                        <span
                          v-else
                          class="text-sm text-gray-500"
                        >
                          {{ t('noOrganization') }}
                        </span>
                      </TableCell>
                      <TableCell>
                        {{ template.form_fields?.length || 0 }}
                      </TableCell>
                      <TableCell>
                        {{ new Date(template.created_at).toLocaleDateString() }}
                      </TableCell>
                      <TableCell>
                        <TableOptionsButton
                          :options="[
                            {
                              label: t('edit'),
                              to: ROUTES.ADMIN.PDF_FORM_TEMPLATES.PDF_FORM_TEMPLATES_DETAIL.replace(
                                ':uuid',
                                template.uuid
                              )
                            },
                            {
                              label: t('reassignOrganization'),
                              onClick: () => openReassignOrgModal(template)
                            },
                            {
                              label: t('delete'),
                              onClick: () => openDeleteModal(template)
                            }
                          ]"
                        />
                      </TableCell>
                    </TableRow>
                  </template>
                </PaginatedTableView>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Delete Confirmation Modal -->
    <Dialog
      :open="showDeleteModal"
      @update:open="closeDeleteModal"
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{{ t('confirmDeletion') }}</DialogTitle>
          <DialogDescription>
            {{ t('confirmDeleteMessage', { name: templateToDelete?.name || '' }) }}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button
            variant="secondary"
            @click="closeDeleteModal"
          >
            {{ t('cancel') }}
          </Button>
          <Button
            variant="destructive"
            @click="confirmDelete"
          >
            {{ t('delete') }}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    <!-- Reassign Organization Modal -->
    <Dialog
      :open="showReassignOrgModal"
      @update:open="closeReassignOrgModal"
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{{ t('reassignOrgTitle') }}</DialogTitle>
          <DialogDescription>
            {{ t('reassignOrgDescription', { name: templateToReassign?.name || '' }) }}
          </DialogDescription>
        </DialogHeader>
        <div class="py-4">
          <SelectField
            v-model="selectedOrgUuid"
            :label="t('organization')"
            name="organization"
            :options="organizationOptions"
            :is-filterable="true"
            :input-action="searchOrganizations"
            :select-option-text="t('selectOrganization')"
            data-cy="PDFFormTemplateListViewOrgSelect"
          />
        </div>
        <DialogFooter>
          <Button
            variant="secondary"
            @click="closeReassignOrgModal"
          >
            {{ t('cancel') }}
          </Button>
          <Button
            variant="default"
            :disabled="!selectedOrgUuid || isReassigning"
            @click="confirmReassignOrg"
          >
            {{ t('confirm') }}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  </div>
</template>
